import { Buffer } from 'buffer';
import { FormikProvider, useFormik } from 'formik';
import _ from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import { Button, Callout, Paragraph, Select, Switch } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';
import Spinner from 'packages/kakapo-ui-kit/components/common/Spinner';

import useLanguage from 'packages/kakapo-ui-application/hooks/useLanguage';
import EmployeesMultiFilter from '../employees/EmployeesFilter';

function EvaluationFormSelector() {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	const query = graphql.useEvaluationFormsQuery({ variables: { isArchived: false, first: 1000 } });

	if (!query.data) {
		return <Spinner isLoading size="20px" />;
	}

	const options = query.data.evaluationForms.edges.map(({ node }) => ({
		value: node.id,
		label: localize(node.title).text,
	}));

	return (
		<EmployeesMultiFilter
			name="evaluationFormIDs"
			label={t('managerInbox.exports.score.evaluationForm')}
			options={_.sortBy(options, 'label')}
		/>
	);
}

function TalentReviewTemplateSelector() {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	const query = graphql.useTalentReviewTemplatesQuery({
		variables: { isArchived: false, first: 1000 },
	});

	if (!query.data) {
		return <Spinner isLoading size="20px" />;
	}

	const options = query.data.talentReviewTemplates.edges.map(({ node }) => ({
		value: node.id,
		label: localize(node.title).text,
	}));

	return (
		<EmployeesMultiFilter
			name="talentReviewTemplateIDs"
			label={t('managerInbox.exports.score.talentReviewTemplate')}
			options={_.sortBy(options, 'label')}
		/>
	);
}

export default function DashboardDownloadScoresOptions(props: {
	startDate: Date;
	endDate: Date;
	onFinish: () => void;
}) {
	const { t } = useTranslation();
	const language = useLanguage();

	const [loading, setLoading] = useState(false);

	const [exportUsers] = graphql.useExportUsersMutation();

	const [format, setFormat] = useState(graphql.ExportFormat.Excel);
	const [group, setGroup] = useState(true);
	const [includeManagerDrafts, setIncludeManagerDrafts] = useState(false);
	const [archivedUsers, setArchivedUsers] = useState(false);

	const formik = useFormik({
		initialValues: {
			evaluationFormIDs: [] as string[],
			talentReviewTemplateIDs: [] as string[],
		},
		onSubmit: async () => {
			setLoading(true);

			const response = await exportUsers({
				variables: {
					input: {
						languageID: language.id,
						conversationTemplateIDs: formik.values.evaluationFormIDs,
						talentReviewTemplateIDs: formik.values.talentReviewTemplateIDs,
						startDate: props.startDate,
						endDate: props.endDate,
						status: !archivedUsers ? graphql.UserStatus.Active : graphql.UserStatus.Archived,
						groupByUser: group,
						includeManagerDrafts,
						format,
					},
				},
			});

			if (response.data) {
				const blob = response.data.exportUsers;

				const link = document.createElement('a');
				const url = URL.createObjectURL(
					new Blob([Buffer.from(blob.data, 'base64')], {
						type: blob.contentType,
					}),
				);

				link.href = url;

				if (format === graphql.ExportFormat.Excel) {
					link.download = 'Overzicht.xlsx';
				} else if (format === graphql.ExportFormat.Csv) {
					link.download = 'Overzicht.csv';
				}

				document.body.appendChild(link);
				link.click();

				URL.revokeObjectURL(url);
				document.body.removeChild(link);
			}

			setLoading(false);

			props.onFinish();
		},
	});

	return (
		<Box p={3} pt={0} minWidth={300}>
			<FormikProvider value={formik}>
				<Box mb={3}>
					<EvaluationFormSelector />
					<TalentReviewTemplateSelector />
					<Flex mt={2} alignItems="center">
						<Box flexGrow={1} flexShrink={1} mr={2}>
							<Paragraph>{t('managerInbox.exports.score.group')}</Paragraph>
						</Box>
						<Switch on={group} onChange={setGroup} />
					</Flex>
					<Flex mt={2} alignItems="center">
						<Box flexGrow={1} flexShrink={1} mr={2}>
							<Paragraph>{t('managerInbox.exports.checkbox.archivedUsers')}</Paragraph>
						</Box>
						<Switch on={archivedUsers} onChange={setArchivedUsers} />
					</Flex>
					<Flex mt={2} alignItems="center">
						<Box flexGrow={1} flexShrink={1} mr={2}>
							<Paragraph>{t('managerInbox.exports.checkbox.includeManagerDrafts')}</Paragraph>
						</Box>
						<Switch on={includeManagerDrafts} onChange={setIncludeManagerDrafts} />
					</Flex>
					<Flex mt={2} alignItems="center">
						<Box flexGrow={1} flexShrink={1} mr={2}>
							<Paragraph>{t('managerInbox.exports.score.format.label')}</Paragraph>
						</Box>
						<Select
							search={false}
							selectedValue={format}
							onChange={(format) => setFormat(format as graphql.ExportFormat)}
							options={[
								{
									value: graphql.ExportFormat.Excel,
									title: t('managerInbox.exports.score.format.excel'),
								},
								{
									value: graphql.ExportFormat.Csv,
									title: t('managerInbox.exports.score.format.csv'),
								},
							]}
						/>
					</Flex>
					{loading && (
						<Callout color="blue" maxWidth={300} mt={3}>
							{t('managerInbox.exports.score.explanation')}
						</Callout>
					)}
				</Box>
				<Button
					loading={loading}
					type="primary"
					title={t('managerInbox.exports.score.download')}
					onClick={() => formik.submitForm()}
					disabled={
						!formik.values.evaluationFormIDs.length && !formik.values.talentReviewTemplateIDs.length
					}
				/>
			</FormikProvider>
		</Box>
	);
}
